/**
 * Shared chart color system for Helion dashboards.
 *
 * Single source of truth for Plotly chart + heatmap colors so the same semantic
 * ("up"/"down", "calm"/"stress", "danger"/"warning") is not re-expressed as
 * different hex in each page. Theme-aware (light/dark) and colorblind-safe:
 * direction is carried by BLUE (up) vs ORANGE (down), never red-vs-green.
 *
 * Currently consumed by Market Snapshot. The per-page palettes in
 * EconomicDashboard.tsx (getEconChartPalette) and MLResultsTab.tsx
 * (getMlChartPalette) are near-duplicates and are the next candidates to fold in.
 */

export type ChartTheme = "light" | "dark";

export type ChartPalette = {
  theme: ChartTheme;
  paperBg: string;
  plotBg: string;
  grid: string;
  axis: string;
  axisMuted: string;
  text: string;
  legendText: string;
  border: string;
  hoverBg: string;
  hoverBorder: string;
  up: string;
  down: string;
  accent: string;
  accentBlue: string;
  accentOrange: string;
  annotationBg: string;
  annotationText: string;
  threshold: string;
  thresholdElevated: string;
  thresholdExtreme: string;
  dailyLine: string;
  dailyLineOpacity: number;
  maLine: string;
  todayMarker: string;
  liveMarker: string;
  zeroLine: string;
};

export const getChartPalette = (theme: ChartTheme): ChartPalette =>
  theme === "dark"
    ? {
        theme,
        paperBg: "rgba(0,0,0,0)",
        plotBg: "rgba(11,18,32,0.86)",
        grid: "rgba(148,163,184,0.14)",
        axis: "rgba(226,232,240,0.72)",
        axisMuted: "rgba(148,163,184,0.68)",
        text: "#f8fafc",
        legendText: "rgba(226,232,240,0.72)",
        border: "rgba(148,163,184,0.18)",
        hoverBg: "#0f172a",
        hoverBorder: "rgba(148,163,184,0.35)",
        up: "rgba(134, 239, 172, 0.92)",
        down: "rgba(248, 113, 113, 0.92)",
        accent: "#f59e0b",
        accentBlue: "#3b82f6",
        accentOrange: "#fb923c",
        annotationBg: "rgba(15,23,42,0.92)",
        annotationText: "#f8fafc",
        threshold: "rgba(203,213,225,0.50)",
        thresholdElevated: "#f59e0b",
        thresholdExtreme: "#ef4444",
        dailyLine: "#60a5fa",
        dailyLineOpacity: 0.48,
        maLine: "#fb923c",
        todayMarker: "#fbbf24",
        liveMarker: "#93c5fd",
        zeroLine: "rgba(226,232,240,0.22)",
      }
    : {
        theme,
        paperBg: "rgba(0,0,0,0)",
        plotBg: "rgba(255,255,255,0.96)",
        grid: "rgba(15,23,42,0.08)",
        axis: "rgba(15,23,42,0.78)",
        axisMuted: "rgba(71,85,105,0.78)",
        text: "#0f172a",
        legendText: "rgba(71,85,105,0.90)",
        border: "rgba(15,23,42,0.08)",
        hoverBg: "#ffffff",
        hoverBorder: "rgba(100,116,139,0.30)",
        up: "rgba(22, 163, 74, 0.90)",
        down: "rgba(220, 38, 38, 0.90)",
        accent: "#d97706",
        accentBlue: "#2563eb",
        accentOrange: "#ea580c",
        annotationBg: "rgba(255,255,255,0.96)",
        annotationText: "#0f172a",
        threshold: "rgba(15,23,42,0.28)",
        thresholdElevated: "#d97706",
        thresholdExtreme: "#dc2626",
        dailyLine: "#2563eb",
        dailyLineOpacity: 0.30,
        maLine: "#ea580c",
        todayMarker: "#d97706",
        liveMarker: "#2563eb",
        zeroLine: "rgba(15,23,42,0.18)",
      };

/* --------------------------------------------------------------------------
 * Heatmap colorscales
 *
 * "stress"    — SEQUENTIAL, for count/intensity grids (e.g. VIX threshold
 *               exceedances). Low end blends into the plot background so calm
 *               cells recede; hot cells ramp amber -> red. Use with zmin: 0.
 *
 * "diverging" — for SIGNED grids (e.g. SPX returns). Traditional finance heatmap:
 *               RED (down) <-> pale neutral <-> GREEN (up). Use with zmid: 0.
 *
 * Both are theme-tuned: near-white calm on light, near-plot-bg calm on dark.
 * ------------------------------------------------------------------------ */

export type HeatmapKind = "stress" | "diverging";

type ColorStop = [number, string];

const STRESS_SCALE: Record<ChartTheme, ColorStop[]> = {
  light: [
    [0.0, "#f1f5f9"], // slate-100 — calm, near light plot bg
    [0.25, "#fde68a"], // amber-200
    [0.5, "#fbbf24"], // amber-400
    [0.75, "#f97316"], // orange-500
    [1.0, "#b91c1c"], // red-700 — hot
  ],
  dark: [
    [0.0, "#0f1a30"], // near plot bg — calm
    [0.25, "#3d3417"], // muted amber
    [0.5, "#a16207"], // amber-700
    [0.75, "#ea580c"], // orange-600
    [1.0, "#ef4444"], // red-500 — hot
  ],
};

const DIVERGING_SCALE: Record<ChartTheme, ColorStop[]> = {
  // Down = RED, Up = GREEN, with a pale neutral midpoint (traditional finance heatmap).
  light: [
    [0.0, "#991b1b"], // red-800 — severe down
    [0.25, "#ef4444"], // red-500
    [0.45, "#fecaca"], // red-200
    [0.5, "#f8fafc"], // pale neutral — flat
    [0.55, "#bbf7d0"], // green-200
    [0.75, "#22c55e"], // green-500
    [1.0, "#15803d"], // green-700 — strong up
  ],
  dark: [
    [0.0, "#b91c1c"], // red-700 — severe down
    [0.25, "#ef4444"], // red-500
    [0.45, "#7f1d1d"], // deep red toward bg
    [0.5, "#0f1a2e"], // near plot bg — flat
    [0.55, "#14532d"], // deep green toward bg
    [0.75, "#22c55e"], // green-500
    [1.0, "#4ade80"], // green-400 — strong up
  ],
};

export const heatmapColorscale = (kind: HeatmapKind, theme: ChartTheme): ColorStop[] =>
  (kind === "diverging" ? DIVERGING_SCALE : STRESS_SCALE)[theme];

/* --------------------------------------------------------------------------
 * Macro / regime chart palettes (Economics + ML dashboards)
 *
 * These pages don't use the SPX/VIX up-down palette above — they encode
 * macro-regime semantics (expansionary / cautionary / recessionary bands,
 * early-warning states). They shared an identical core (axis/grid/zero chrome
 * + green/amber/red status hues + link) that was copy-pasted in both files;
 * that core now lives once in macroChartBase() and each page's palette is
 * built from it. Values are unchanged from the originals — pure de-dup.
 * ------------------------------------------------------------------------ */

type MacroChartBase = {
  axisText: string;
  mutedText: string;
  gridLine: string;
  zeroLine: string;
  expansionary: string;
  cautionary: string;
  recessionary: string;
  link: string;
};

const macroChartBase = (isDark: boolean): MacroChartBase => ({
  axisText: isDark ? "#cbd5e1" : "#475569",
  mutedText: isDark ? "#94a3b8" : "#64748b",
  gridLine: isDark ? "rgba(148, 163, 184, 0.24)" : "rgba(148, 163, 184, 0.28)",
  zeroLine: isDark ? "rgba(148, 163, 184, 0.30)" : "rgba(148, 163, 184, 0.32)",
  expansionary: isDark ? "#86efac" : "#16a34a",
  cautionary: isDark ? "#fcd34d" : "#ca8a04",
  recessionary: isDark ? "#fca5a5" : "#dc2626",
  link: isDark ? "#93c5fd" : "#2563eb",
});

export type EconChartPalette = MacroChartBase & {
  thresholdDefault: string;
  rawWeakening: string;
  rollingWeakening: string;
  indicatorLine: string;
  sentinel: string;
  monitor: string;
  watch: string;
  caution: string;
  severe: string;
};

export function getEconChartPalette(theme: ChartTheme | string): EconChartPalette {
  const isDark = theme === "dark";
  return {
    ...macroChartBase(isDark),
    thresholdDefault: isDark ? "rgba(203, 213, 225, 0.72)" : "rgba(100, 116, 139, 0.72)",
    rawWeakening: isDark ? "#60a5fa" : "#2563eb",
    rollingWeakening: isDark ? "#f8fafc" : "#0f172a",
    indicatorLine: isDark ? "#60a5fa" : "#2563eb",
    sentinel: isDark ? "rgba(250, 204, 21, 0.78)" : "rgba(202, 138, 4, 0.72)",
    monitor: isDark ? "rgba(203, 213, 225, 0.74)" : "rgba(100, 116, 139, 0.76)",
    watch: isDark ? "rgba(251, 191, 36, 0.90)" : "rgba(202, 138, 4, 0.90)",
    caution: isDark ? "rgba(251, 146, 60, 0.92)" : "rgba(234, 88, 12, 0.90)",
    severe: isDark ? "rgba(248, 113, 113, 0.94)" : "rgba(220, 38, 38, 0.90)",
  };
}

export type MlChartPalette = MacroChartBase & {
  tableAlt: string;
};

export function getMlChartPalette(theme: ChartTheme | string): MlChartPalette {
  const isDark = theme === "dark";
  return {
    ...macroChartBase(isDark),
    tableAlt: "rgba(148, 163, 184, 0.08)",
  };
}
